/**
 * ArtefactExportDialog Component
 *
 * Dialog for batch exporting multiple artefacts to various formats (PowerPoint, Word, PDF).
 * Features drag-and-drop reordering for customizing export order.
 *
 * Note: The @hello-pangea/dnd library may show a nested scroll container warning in development.
 * This is a known limitation when using drag-and-drop inside modals with scrollable content.
 * The warning is development-only and does not affect functionality or production builds.
 * See: https://github.com/atlassian/react-beautiful-dnd/issues/131
 */

import type { Brand } from "@webuild-ai/react-auth";

import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Card,
  CardBody,
  Chip,
  Input,
  Switch,
  Tooltip,
} from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-auth";

import { Artefact, ARTEFACT_TYPE_META } from "@/types/artefacts";
import {
  BatchExportFormat,
  batchExportToPDFBlob,
  batchExportToWordBlob,
} from "@/utils/artefactExport";
import { useTheme } from "@/hooks/use-theme";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { DEFAULT_FONT, extractPrimaryFont } from "@/utils/fontUtils";
import { logger } from "@/telemetry";

import { WordPreviewModal } from "./WordPreviewModal";
import { PDFPreviewModal } from "./PDFPreviewModal";

export interface ArtefactExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  artefacts: Artefact[];
  onExport: (
    artefacts: Artefact[],
    format: BatchExportFormat,
    title?: string,
    useSeparateWorksheets?: boolean,
    fontFamily?: string,
    codeFontFamily?: string,
  ) => Promise<void>;
  brand: Brand;
  theme: "light" | "dark";
}

const getExportFormats = (t: (key: string) => string) => [
  {
    key: "word",
    label: t("Word Document"),
    shortLabel: t("Word"),
    icon: "vscode-icons:file-type-word",
    extension: ".docx",
    description: t("Full document with formatted content"),
  },
  {
    key: "pdf",
    label: t("PDF Document"),
    shortLabel: t("PDF"),
    icon: "vscode-icons:file-type-pdf2",
    extension: ".pdf",
    description: t("Portable document with text selection"),
  },
  {
    key: "powerpoint",
    label: t("PowerPoint Presentation"),
    shortLabel: t("PowerPoint"),
    icon: "vscode-icons:file-type-powerpoint",
    extension: ".pptx",
    description: t("Slides with headings as section dividers"),
  },
  {
    key: "excel",
    label: t("Excel Spreadsheet"),
    shortLabel: t("Excel"),
    icon: "vscode-icons:file-type-excel",
    extension: ".xlsx",
    description: t("Spreadsheet with tables and formatted data"),
  },
];

export const ArtefactExportDialog: React.FC<ArtefactExportDialogProps> = ({
  isOpen,
  onClose,
  artefacts: initialArtefacts,
  onExport,
  brand: _brand,
  theme: _theme,
}) => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { isDark } = useTheme();
  const { exportFontFamily } = useUIPreferences();

  /**
   * Get the effective font family for Word exports based on user preference
   * "brand" = use brand font, otherwise use the explicitly selected font.
   * Uses extractPrimaryFont() to handle CSS font-family strings like "Raleway,sans-serif"
   * since the docx library expects a single font name, not a font stack.
   */
  const getExportFontFamily = (): string => {
    if (exportFontFamily === "brand") {
      return extractPrimaryFont(brand.fontFamily || DEFAULT_FONT);
    }

    return extractPrimaryFont(exportFontFamily);
  };
  const [format, setFormat] = useState<BatchExportFormat>("word");
  const [orderedArtefacts, setOrderedArtefacts] = useState<Artefact[]>(initialArtefacts);
  const [isExporting, setIsExporting] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [useSeparateWorksheets, setUseSeparateWorksheets] = useState(false);

  // PDF Preview state
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [isGeneratingPdfPreview, setIsGeneratingPdfPreview] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState<ArrayBuffer | null>(null);

  // Word Preview state
  const [isWordPreviewOpen, setIsWordPreviewOpen] = useState(false);
  const [isGeneratingWordPreview, setIsGeneratingWordPreview] = useState(false);
  const [wordPreviewData, setWordPreviewData] = useState<ArrayBuffer | null>(null);

  // Suppress @hello-pangea/dnd nested scroll container warning in development
  useEffect(() => {
    if (isOpen && import.meta.env.DEV) {
      const originalWarn = console.warn;

      console.warn = (...args: any[]) => {
        // Suppress the specific drag-and-drop nested scroll warning
        if (
          typeof args[0] === "string" &&
          args[0].includes("@hello-pangea/dnd") &&
          args[0].includes("nested scroll container")
        ) {
          return;
        }
        originalWarn.apply(console, args);
      };

      return () => {
        console.warn = originalWarn;
      };
    }
  }, [isOpen]);

  // Reset state when dialog opens with new artefacts
  useEffect(() => {
    if (isOpen) {
      setOrderedArtefacts(initialArtefacts);
      setFormat("word");
      setIsExporting(false);
      setCustomTitle("");
      setUseSeparateWorksheets(false);
      // Reset PDF preview state
      setPdfPreviewData(null);
      setIsPdfPreviewOpen(false);
      // Reset Word preview state
      setWordPreviewData(null);
      setIsWordPreviewOpen(false);
    }
  }, [isOpen, initialArtefacts]);

  // Handle drag-and-drop reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedArtefacts);
    const [reorderedItem] = items.splice(result.source.index, 1);

    items.splice(result.destination.index, 0, reorderedItem);
    setOrderedArtefacts(items);
  };

  // Handle removing an artefact from the export list
  const handleRemoveArtefact = (artefactId: string) => {
    setOrderedArtefacts((prev) => prev.filter((a) => a.id !== artefactId));
  };

  // Handle export button click
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExport(
        orderedArtefacts,
        format,
        customTitle || undefined,
        format === "excel" ? useSeparateWorksheets : undefined,
        getExportFontFamily(),
        brand.codeFontFamily,
      );
      onClose();
    } catch (error) {
      logger.error("Export failed", error);
      // Error toast is handled by parent
    } finally {
      setIsExporting(false);
    }
  };

  // Handle PDF preview
  const handlePdfPreview = async () => {
    setIsGeneratingPdfPreview(true);
    try {
      const pdfData = await batchExportToPDFBlob(orderedArtefacts, {
        title: customTitle || undefined,
      });

      setPdfPreviewData(pdfData);
      setIsPdfPreviewOpen(true);
    } catch (error) {
      logger.error("PDF preview generation failed", error);
    } finally {
      setIsGeneratingPdfPreview(false);
    }
  };

  // Handle Word preview
  const handleWordPreview = async () => {
    setIsGeneratingWordPreview(true);
    try {
      // Use export font preference (user-selected or brand default)
      const wordData = await batchExportToWordBlob(orderedArtefacts, {
        title: customTitle || undefined,
        fontFamily: getExportFontFamily(),
        codeFontFamily: brand.codeFontFamily,
      });

      setWordPreviewData(wordData);
      setIsWordPreviewOpen(true);
    } catch (error) {
      logger.error("Word preview generation failed", error);
    } finally {
      setIsGeneratingWordPreview(false);
    }
  };

  // Handle download from PDF preview modal
  const handleDownloadFromPdfPreview = () => {
    if (!pdfPreviewData) return;

    const blob = new Blob([pdfPreviewData], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
    const filename = `batch_export_${timestamp[0]}_${timestamp[1].substring(0, 8)}.pdf`;

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setIsPdfPreviewOpen(false);
    onClose();
  };

  // Handle download completion from Word preview modal (download already performed by modal)
  const handleDownloadFromWordPreview = () => {
    setIsWordPreviewOpen(false);
  };

  const EXPORT_FORMATS = getExportFormats(t);
  const selectedFormatInfo = EXPORT_FORMATS.find((f) => f.key === format);

  return (
    <Modal
      isDismissable
      classNames={{
        base: "max-h-[90vh]",
        body: "py-6",
      }}
      isKeyboardDismissDisabled={false}
      isOpen={isOpen}
      size="3xl"
      onClose={onClose}
    >
      <ModalContent>
        {(onCloseModal) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Icon icon="solar:export-bold" width={24} />
                <span>{t("Export Generated Content")}</span>
              </div>
              <p className="text-sm font-normal text-default-500">
                {t("Choose format and arrange content order")}
              </p>
            </ModalHeader>
            <ModalBody>
              {/* Document Title */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-default-700">
                  {t("Document Title")}
                </label>
                <Input
                  autoFocus
                  classNames={{
                    input: "text-sm",
                  }}
                  placeholder={t("Enter document title (optional)")}
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                />
              </div>

              {/* Format Selection */}
              <div className="space-y-2 mt-4">
                <label className="text-sm font-medium text-default-700">{t("Export Format")}</label>
                <Select
                  aria-label={t("Export format")}
                  classNames={{
                    trigger: "shadow-md",
                  }}
                  description={selectedFormatInfo?.description}
                  placeholder={t("Select format")}
                  selectedKeys={[format]}
                  startContent={<Icon icon={selectedFormatInfo?.icon || ""} width={20} />}
                  onChange={(e) => setFormat(e.target.value as BatchExportFormat)}
                >
                  {EXPORT_FORMATS.map((fmt) => (
                    <SelectItem
                      key={fmt.key}
                      startContent={<Icon icon={fmt.icon} width={20} />}
                      textValue={fmt.label}
                    >
                      <div className="flex flex-col">
                        <span>{fmt.label}</span>
                        <span className="text-xs text-default-400">{fmt.extension}</span>
                      </div>
                    </SelectItem>
                  ))}
                </Select>
              </div>

              {/* Excel-specific options */}
              {format === "excel" && (
                <div
                  className={`mt-4 p-4 rounded-lg border-2 transition-all ${
                    useSeparateWorksheets
                      ? "bg-brand-primary/5 border-brand-primary"
                      : "bg-default-100 dark:bg-default-50 border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Switch
                      classNames={{
                        wrapper: "group-data-[selected=true]:bg-brand-primary",
                      }}
                      isSelected={useSeparateWorksheets}
                      size="sm"
                      onValueChange={setUseSeparateWorksheets}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {t("Export to separate worksheets")}
                        </span>
                        <span className="text-xs text-default-500">
                          {t("Create one worksheet per artefact instead of combining all")}
                        </span>
                      </div>
                    </Switch>
                    {useSeparateWorksheets && (
                      <Icon
                        className="text-brand-primary flex-shrink-0"
                        icon="solar:check-circle-bold"
                        width={20}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Artefacts Reordering */}
              <div className="space-y-2 mt-4">
                <label className="text-sm font-medium text-default-700">
                  {t("Content Order ({{count}} items)", { count: orderedArtefacts.length })}
                </label>
                <p className="text-xs text-default-500">
                  {t("Drag to reorder • Click X to remove from export")}
                </p>

                {orderedArtefacts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Icon
                      className="text-default-300 mb-2"
                      icon="solar:inbox-line-bold"
                      width={48}
                    />
                    <p className="text-sm text-default-500">{t("No items to export")}</p>
                    <p className="text-xs text-default-400 mt-1">
                      {t("All items have been removed")}
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[400px] overflow-y-auto">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="artefacts">
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            className="space-y-2"
                            {...provided.droppableProps}
                          >
                            {orderedArtefacts.map((artefact, index) => {
                              const typeMeta = ARTEFACT_TYPE_META[artefact.type];

                              return (
                                <Draggable
                                  key={artefact.id}
                                  draggableId={artefact.id}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <Card
                                      ref={provided.innerRef}
                                      className={snapshot.isDragging ? "opacity-70 shadow-lg" : ""}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <CardBody className="p-3">
                                        <div className="flex items-center gap-3">
                                          <Icon
                                            className="text-default-400 cursor-grab active:cursor-grabbing flex-shrink-0"
                                            icon="solar:hamburger-menu-line-duotone"
                                            width={20}
                                          />
                                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-default-100 text-xs font-medium text-default-500 flex-shrink-0">
                                            {index + 1}
                                          </div>
                                          <div
                                            className={`p-2 rounded-lg bg-default-100 ${typeMeta.color} flex-shrink-0`}
                                          >
                                            <Icon height={16} icon={typeMeta.icon} width={16} />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm font-medium text-default-800 truncate">
                                                {artefact.title}
                                              </span>
                                              <Chip
                                                className="flex-shrink-0"
                                                size="sm"
                                                variant="flat"
                                              >
                                                {typeMeta.label}
                                              </Chip>
                                            </div>
                                          </div>
                                          <Button
                                            isIconOnly
                                            aria-label={t("Remove {{title}}", {
                                              title: artefact.title,
                                            })}
                                            className="flex-shrink-0"
                                            color="danger"
                                            size="sm"
                                            variant="light"
                                            onPress={() => handleRemoveArtefact(artefact.id)}
                                          >
                                            <Icon icon="solar:close-circle-bold" width={20} />
                                          </Button>
                                        </div>
                                      </CardBody>
                                    </Card>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                style={{
                  backgroundColor: isDark
                    ? brand.colors.dark.tertiary
                    : brand.colors.light.tertiary,
                  color: "white",
                }}
                variant="solid"
                onPress={onCloseModal}
              >
                {t("Cancel")}
              </Button>
              {format === "pdf" && (
                <Tooltip content={t("Preview PDF before downloading")}>
                  <Button
                    isDisabled={orderedArtefacts.length === 0}
                    isLoading={isGeneratingPdfPreview}
                    startContent={
                      !isGeneratingPdfPreview && <Icon icon="solar:eye-bold" width={20} />
                    }
                    style={{
                      backgroundColor: isDark
                        ? brand.colors.dark.tertiary
                        : brand.colors.light.tertiary,
                      color: "white",
                    }}
                    variant="solid"
                    onPress={handlePdfPreview}
                  >
                    {isGeneratingPdfPreview ? t("Generating...") : t("Preview")}
                  </Button>
                </Tooltip>
              )}
              {format === "word" && (
                <Tooltip content={t("Preview Word document before downloading")}>
                  <Button
                    isDisabled={orderedArtefacts.length === 0}
                    isLoading={isGeneratingWordPreview}
                    startContent={
                      !isGeneratingWordPreview && <Icon icon="solar:eye-bold" width={20} />
                    }
                    style={{
                      backgroundColor: isDark
                        ? brand.colors.dark.tertiary
                        : brand.colors.light.tertiary,
                      color: "white",
                    }}
                    variant="solid"
                    onPress={handleWordPreview}
                  >
                    {isGeneratingWordPreview ? t("Generating...") : t("Preview")}
                  </Button>
                </Tooltip>
              )}
              <Tooltip content={t("Download")}>
                <Button
                  className="btn-brand-primary"
                  isDisabled={orderedArtefacts.length === 0}
                  isLoading={isExporting}
                  startContent={!isExporting && <Icon icon="solar:export-bold" width={20} />}
                  onPress={handleExport}
                >
                  {isExporting
                    ? t("Exporting...")
                    : t("Export to {{format}}", {
                        format: selectedFormatInfo?.shortLabel || t("Document"),
                      })}
                </Button>
              </Tooltip>
            </ModalFooter>
          </>
        )}
      </ModalContent>

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={isPdfPreviewOpen}
        pdfData={pdfPreviewData}
        title={customTitle || t("Generated Content Export")}
        onClose={() => setIsPdfPreviewOpen(false)}
        onDownload={handleDownloadFromPdfPreview}
      />

      {/* Word Preview Modal */}
      <WordPreviewModal
        isLoading={isGeneratingWordPreview}
        isOpen={isWordPreviewOpen}
        title={customTitle || t("Generated Content Export")}
        wordData={wordPreviewData}
        onClose={() => setIsWordPreviewOpen(false)}
        onDownload={handleDownloadFromWordPreview}
      />
    </Modal>
  );
};
